'use client';
import React from 'react'
import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Box,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme
} from '@mui/material';
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable
} from '@tanstack/react-table';
import Buttons from 'src/components/era-package/era/Buttons';
import Button from 'src/components/era-package/forms/Button';
import EditModal from 'src/components/era-package/forms/EditModal';
import { useDataTable } from 'src/utils/era-package/dataTable';

const DataTableSection = ({
  title,
  columns,
  fields, name, error,
  optionsMap,
  data = [],
  onDataChange,
  maxRows,
  setHasUnsaved,
  disableEdit = false,
  onRowClick, // New prop for custom row click handler
}) => {
  const theme = useTheme();

  const {
    isModalOpen,
    editingRow,
    editingIndex,
    generatedColumns,
    handleAddRow,
    handleEditRow,
    handleSave,
    handleDelete,
    handleCloseModal,
  } = useDataTable({
    data,
    onDataChange,
    fields,
    columns,
    optionsMap,
    setHasUnsaved,
  });

  const table = useReactTable({
    data,
    columns: generatedColumns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <Stack spacing={2}>
      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableCell
                    key={header.id}
                    sx={{
                      fontWeight: 600,
                      color: theme.palette.primary.main,
                      cursor: header.column.getCanSort() ? 'pointer' : 'default',
                      userSelect: 'none',
                      '&:hover': {
                        backgroundColor: header.column.getCanSort()
                          ? theme.palette.action.hover
                          : 'transparent',
                      },
                    }}
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      {header.column.getIsSorted() && (
                        <Box sx={{ ml: 0.5, fontSize: '0.75rem' }}>
                          {header.column.getIsSorted() === 'asc' ? '↑' : '↓'}
                        </Box>
                      )}
                    </Box>
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableHead>
          <TableBody>
            {table.getRowModel().rows.map((row, index) => (
              <TableRow
                key={row.id}
                hover={!disableEdit || onRowClick}
                onClick={
                  onRowClick
                    ? () => onRowClick(row.original, index)
                    : disableEdit
                      ? undefined
                      : () => handleEditRow(row.original, index)
                }
                sx={{ cursor: (disableEdit && !onRowClick) ? 'default' : 'pointer' }}
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))}
            {data.length === 0 && (
              <TableRow>
                <TableCell colSpan={generatedColumns.length} align="center">
                  <Typography variant="body2" color={theme.palette.info.main}>
                    No {title} records{!disableEdit ? '. Click "Add" to add one.' : '.'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Buttons
        other={error}
        advance={
          maxRows && data.length >= maxRows
            ?
            <Typography variant="body2" color={theme.palette.info.main}>
              Maximum {maxRows} {maxRows === 1 ? 'record' : 'records'} allowed
            </Typography>
            :
            !disableEdit &&
            <Button
              size="medium"
              minWidth={80}
              startIcon={<FontAwesomeIcon icon={faPlus} width={12} />}
              onClick={handleAddRow}
              title={`Add a new ${title} record`}
            >
              Add
            </Button>
        }
      />

      <EditModal
        title={title}
        open={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleSave}
        onDelete={handleDelete}
        fields={fields}
        optionsMap={optionsMap}
        initialData={editingRow}
        isEditing={editingIndex !== null}
        index={editingIndex}
        name={name}
      />
    </Stack>
  );
};

export default DataTableSection;
